/**
 * Settings panel — slides up from the bottom.
 *
 * Contains:
 * - Gateway list (multi-gateway management)
 * - TTS provider selector (Edge TTS / ElevenLabs / Web Speech)
 * - Edge TTS backend config (URL + auth token)
 * - STT provider indicator
 * - OpenAI API Key input (for Whisper STT)
 * - Model selector
 * - Playback speed control
 * - VAD sensitivity slider
 * - Privacy mode toggle
 * - Timestamps toggle
 * - Environment switching (production/staging)
 * - Version info (frontend, backend, Android, gateway)
 */

import { useState, useCallback, useEffect } from 'react';
import { ModelSelector } from './ModelSelector';
import { GatewayList } from './GatewayList';
import { STORAGE_KEYS, SPEED_STEPS } from '../utils/constants';
import {
  type Environment,
  getEnvironment,
  getApiUrl,
  setEnvironmentOverride,
  isEnvironmentOverridden,
  detectEnvironment,
} from '../utils/environment';
import { useVersionInfo } from '../hooks/useVersionInfo';
import { readString, writeString, remove } from '../utils/storage';
import {
  beginPreviewActivation,
  buildPreviewBasePath,
  buildStableUrl,
  clearPendingPreviewActivation,
  clearPreviewActivationFailure,
  clearRequestedPreviewPr,
  clearSelectedPreviewPr,
  fetchPreviewBuildVersion,
  getCurrentPreviewPrNumber,
  getPreviewActivationFailure,
  getRequestedPreviewPr,
  setRequestedPreviewPr,
} from '../utils/previewBuild';
import { DEBUG_LOGGING_KEY, setRemoteLoggingEnabled } from '../utils/remoteLog';
import type { SttProvider, SttEngine } from '../voice/stt';
import { getSttEngine, defaultSttEngine, isWebSpeechAvailable, hasOpenAIKey } from '../voice/stt';
import type { GatewayConfig, GatewayStatus, TtsProvider } from '../types/gateway';
import type { WSClient } from '../ws/client';
import { getTtsProvider } from '../voice/tts';
import { CloudSettings } from './CloudSettings';
import { PagesProvisioningSettings } from './PagesProvisioningSettings';
import { GitHubSettingsWithBoundary } from './GitHubSettingsErrorBoundary';
import { VoiceLockSettings } from './VoiceLockSettings';
import { LanguageVoiceSettings } from './LanguageVoiceSettings';
import type { UseVoiceLockReturn } from '../hooks/useVoiceLock';
import { useAuth } from '../auth/AuthContext';
import { reloadApp } from '../utils/reload';
import { checkForAppUpdate } from '../utils/updateCheck';
import { useSetting } from '../settings';
import { normalizeSendWhileRunningMode } from '../chat/sendWhileRunning';
import styles from './Settings.module.css';

// ── Props ──

interface SettingsProps {
  /** Whether the settings panel is visible */
  isOpen: boolean;
  /** Close the settings panel */
  onClose: () => void;
  /** Currently selected model */
  selectedModel?: string;
  /** Called when model changes */
  onModelChange?: (model: string) => void;
  /** Whether privacy mode is active */
  privacyMode?: boolean;
  /** Toggle privacy mode */
  onPrivacyToggle?: () => void;
  /** Whether timestamps are shown */
  showTimestamps?: boolean;
  /** Toggle timestamp display */
  onTimestampsToggle?: () => void;
  /** Gateway connection status text */
  connectionStatus?: string;
  /** Raw focused-gateway connection state */
  gatewayConnectionState?: GatewayStatus;
  /** Whether a request is in progress (disables some controls) */
  isRequestActive?: boolean;
  /** Active STT provider */
  sttProvider?: SttProvider;
  /** Called when API key changes (so voice hook can refresh) */
  onApiKeyChange?: () => void;
  // ── Multi-gateway props ──
  /** All configured gateways */
  gateways?: GatewayConfig[];
  /** Currently focused gateway ID */
  activeGatewayId?: string | null;
  /** Live connection states per gateway */
  connectionStates?: Map<string, GatewayStatus>;
  /** Called when a gateway is selected (switch focus) */
  onGatewaySelect?: (id: string) => void;
  /** Called when a gateway should be removed */
  onGatewayRemove?: (id: string) => void;
  /** Called when the user wants to add a new gateway */
  onGatewayAdd?: () => void;
  /** Called when the user wants to sign in (shows login flow) */
  onSignIn?: () => void;
  /** Gateway version from hello-ok server.version */
  gatewayVersion?: string | null;
  /** Active gateway WS client for direct config install checks */
  gatewayClient?: WSClient | null;
  /** Active gateway display name */
  activeGatewayName?: string | null;
  /** Active session key on the focused gateway */
  activeSessionKey?: string | null;
  /** Voice Lock hook return (optional — only passed when available) */
  voiceLock?: UseVoiceLockReturn;
}

// ── OpenAI API Key Input ──

function OpenAIKeyInput({
  onKeyChange,
}: {
  onKeyChange?: () => void;
}) {
  const [apiKey, setApiKey] = useState(() =>
    readString(STORAGE_KEYS.OPENAI_API_KEY, ''),
  );
  const [showKey, setShowKey] = useState(false);

  const handleSave = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      setApiKey(trimmed);
      if (trimmed) {
        writeString(STORAGE_KEYS.OPENAI_API_KEY, trimmed);
      } else {
        remove(STORAGE_KEYS.OPENAI_API_KEY);
      }
      onKeyChange?.();
    },
    [onKeyChange],
  );

  const handleClear = useCallback(() => {
    setApiKey('');
    remove(STORAGE_KEYS.OPENAI_API_KEY);
    onKeyChange?.();
  }, [onKeyChange]);

  return (
    <div className={styles.apiKeyContainer}>
      <div className={styles.apiKeyInputRow}>
        <input
          type={showKey ? 'text' : 'password'}
          className={styles.apiKeyInput}
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          onBlur={(e) => handleSave(e.target.value)}
          placeholder="sk-..."
          autoComplete="off"
          spellCheck={false}
        />
        <button
          className={styles.apiKeyToggle}
          onClick={() => setShowKey((v) => !v)}
          aria-label={showKey ? 'Hide API key' : 'Show API key'}
          type="button"
        >
          {showKey ? '🙈' : '👁️'}
        </button>
        {apiKey && (
          <button
            className={styles.apiKeyClear}
            onClick={handleClear}
            aria-label="Clear API key"
            type="button"
          >
            ✕
          </button>
        )}
      </div>
      <span className={styles.apiKeyHint}>
        {apiKey
          ? 'Whisper STT active — voice uses OpenAI'
          : 'Add key to enable Whisper STT'}
      </span>
    </div>
  );
}

// ── STT Engine Selector ──

function SttEngineSelector({
  onEngineChange,
  refreshTrigger,
}: {
  onEngineChange?: () => void;
  refreshTrigger?: number;
}) {
  const [engine, setEngine] = useState<SttEngine>(
    () => getSttEngine() ?? defaultSttEngine(),
  );
  const [whisperOk, setWhisperOk] = useState(() => hasOpenAIKey());
  const webSpeechOk = isWebSpeechAvailable(); // static — won't change at runtime

  // Re-check Whisper availability when API key changes (refreshTrigger)
  useEffect(() => {
    const available = hasOpenAIKey();
    setWhisperOk(available);

    // Auto-switch away from an unavailable engine
    const current = getSttEngine() ?? defaultSttEngine();
    if (current === 'whisper' && !available && webSpeechOk) {
      setEngine('web-speech');
      writeString(STORAGE_KEYS.STT_ENGINE, 'web-speech');
      onEngineChange?.();
    } else if (current === 'web-speech' && !webSpeechOk && available) {
      setEngine('whisper');
      writeString(STORAGE_KEYS.STT_ENGINE, 'whisper');
      onEngineChange?.();
    }
  }, [refreshTrigger, onEngineChange, webSpeechOk]);

  const handleChange = useCallback(
    (value: SttEngine) => {
      if (value === 'whisper' && !hasOpenAIKey()) return;
      if (value === 'web-speech' && !isWebSpeechAvailable()) return;
      setEngine(value);
      writeString(STORAGE_KEYS.STT_ENGINE, value);
      onEngineChange?.();
    },
    [onEngineChange],
  );

  return (
    <div className={styles.engineSelector}>
      <button
        className={`${styles.engineOption} ${engine === 'whisper' ? styles.engineActive : ''} ${!whisperOk ? styles.engineDisabled : ''}`}
        onClick={() => handleChange('whisper')}
        type="button"
        disabled={!whisperOk}
      >
        🎙 Whisper
        {!whisperOk && <span className={styles.engineHint}>API key required</span>}
      </button>
      <button
        className={`${styles.engineOption} ${engine === 'web-speech' ? styles.engineActive : ''} ${!webSpeechOk ? styles.engineDisabled : ''}`}
        onClick={() => handleChange('web-speech')}
        type="button"
        disabled={!webSpeechOk}
      >
        🌐 Browser
        {!webSpeechOk && <span className={styles.engineHint}>Not supported</span>}
      </button>
    </div>
  );
}

// ── STT Provider Badge ──

function SttProviderBadge({ provider }: { provider: SttProvider }) {
  const labels: Record<SttProvider, string> = {
    whisper: '🎙 Whisper',
    'web-speech': '🌐 Web Speech',
    none: '🔇 No STT',
  };

  const className =
    provider === 'whisper'
      ? styles.sttBadgeWhisper
      : provider === 'web-speech'
        ? styles.sttBadgeWebSpeech
        : styles.sttBadgeNone;

  return (
    <span className={`${styles.sttBadge} ${className}`}>
      {labels[provider]}
    </span>
  );
}

// ── TTS Provider Selector ──

function TtsProviderSelector({
  onProviderChange,
}: {
  onProviderChange?: () => void;
}) {
  const [provider, setProvider] = useState<TtsProvider>(() => getTtsProvider());

  const handleChange = useCallback(
    (value: TtsProvider) => {
      setProvider(value);
      writeString(STORAGE_KEYS.TTS_PROVIDER, value);
      onProviderChange?.();
    },
    [onProviderChange],
  );

  return (
    <div className={styles.engineSelector}>
      <button
        className={`${styles.engineOption} ${provider === 'edge' ? styles.engineActive : ''}`}
        onClick={() => handleChange('edge')}
        type="button"
      >
        🌊 Edge
      </button>
      <button
        className={`${styles.engineOption} ${provider === 'elevenlabs' ? styles.engineActive : ''}`}
        onClick={() => handleChange('elevenlabs')}
        type="button"
      >
        🔊 ElevenLabs
      </button>
      <button
        className={`${styles.engineOption} ${provider === 'webspeech' ? styles.engineActive : ''}`}
        onClick={() => handleChange('webspeech')}
        type="button"
      >
        🌐 Browser
      </button>
    </div>
  );
}

// ── Edge TTS Config ──

function EdgeTtsConfigInput({
  onConfigChange,
}: {
  onConfigChange?: () => void;
}) {
  const [backendUrl, setBackendUrl] = useState(() =>
    readString(STORAGE_KEYS.EDGE_TTS_BACKEND_URL, getApiUrl()),
  );
  const [authToken, setAuthToken] = useState(() =>
    readString(STORAGE_KEYS.EDGE_TTS_AUTH_TOKEN, ''),
  );
  const [showToken, setShowToken] = useState(false);

  const handleUrlSave = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      setBackendUrl(trimmed);
      if (trimmed) {
        writeString(STORAGE_KEYS.EDGE_TTS_BACKEND_URL, trimmed);
      } else {
        remove(STORAGE_KEYS.EDGE_TTS_BACKEND_URL);
      }
      onConfigChange?.();
    },
    [onConfigChange],
  );

  const handleTokenSave = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      setAuthToken(trimmed);
      if (trimmed) {
        writeString(STORAGE_KEYS.EDGE_TTS_AUTH_TOKEN, trimmed);
      } else {
        remove(STORAGE_KEYS.EDGE_TTS_AUTH_TOKEN);
      }
      onConfigChange?.();
    },
    [onConfigChange],
  );

  const handleTokenClear = useCallback(() => {
    setAuthToken('');
    remove(STORAGE_KEYS.EDGE_TTS_AUTH_TOKEN);
    onConfigChange?.();
  }, [onConfigChange]);

  return (
    <div className={styles.apiKeyContainer}>
      {/* Backend URL */}
      <div className={styles.apiKeyInputRow}>
        <input
          type="text"
          className={styles.apiKeyInput}
          value={backendUrl}
          onChange={(e) => setBackendUrl(e.target.value)}
          onBlur={(e) => handleUrlSave(e.target.value)}
          placeholder="Backend URL"
          autoComplete="off"
          spellCheck={false}
        />
      </div>
      <span className={styles.apiKeyHint}>Backend URL</span>

      {/* Auth Token */}
      <div className={styles.apiKeyInputRow}>
        <input
          type={showToken ? 'text' : 'password'}
          className={styles.apiKeyInput}
          value={authToken}
          onChange={(e) => setAuthToken(e.target.value)}
          onBlur={(e) => handleTokenSave(e.target.value)}
          placeholder="Auth token…"
          autoComplete="off"
          spellCheck={false}
        />
        <button
          className={styles.apiKeyToggle}
          onClick={() => setShowToken((v) => !v)}
          aria-label={showToken ? 'Hide token' : 'Show token'}
          type="button"
        >
          {showToken ? '🙈' : '👁️'}
        </button>
        {authToken && (
          <button
            className={styles.apiKeyClear}
            onClick={handleTokenClear}
            aria-label="Clear auth token"
            type="button"
          >
            ✕
          </button>
        )}
      </div>
      <span className={styles.apiKeyHint}>
        {authToken
          ? 'Edge TTS configured ✓'
          : 'Add token to enable Edge TTS'}
      </span>
    </div>
  );
}

// ── Speed Control ──

function SpeedControl() {
  const [speedIndex, setSpeedIndex] = useState<number>(() => {
    const saved = readString(STORAGE_KEYS.PLAYBACK_SPEED, '1');
    const idx = SPEED_STEPS.indexOf(parseFloat(saved) as (typeof SPEED_STEPS)[number]);
    return idx >= 0 ? idx : 0;
  });

  const cycleSpeed = useCallback(() => {
    setSpeedIndex((prev) => {
      const next = (prev + 1) % SPEED_STEPS.length;
      writeString(STORAGE_KEYS.PLAYBACK_SPEED, String(SPEED_STEPS[next]));
      return next;
    });
  }, []);

  return (
    <button
      className={styles.speedBtn}
      onClick={cycleSpeed}
      aria-label={`Playback speed: ${SPEED_STEPS[speedIndex]}x`}
    >
      {SPEED_STEPS[speedIndex]}×
    </button>
  );
}

// ── VAD Sensitivity ──

function VadSensitivity() {
  const [threshold, setThreshold] = useState<number>(() => {
    const saved = readString(STORAGE_KEYS.VAD_THRESHOLD, '0.8');
    return parseFloat(saved);
  });

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = parseFloat(e.target.value);
      setThreshold(val);
      writeString(STORAGE_KEYS.VAD_THRESHOLD, String(val));
    },
    [],
  );

  return (
    <div className={styles.sliderContainer}>
      <input
        type="range"
        min="0.1"
        max="1.0"
        step="0.05"
        value={threshold}
        onChange={handleChange}
        className={styles.slider}
        aria-label="VAD sensitivity"
      />
      <span className={styles.sliderValue}>
        {threshold.toFixed(2)}
      </span>
    </div>
  );
}

// ── Toggle Switch ──

function ToggleSwitch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <label className={styles.toggle}>
      <span className={styles.toggleLabel}>{label}</span>
      <div
        className={`${styles.toggleTrack} ${checked ? styles.toggleActive : ''}`}
        onClick={onChange}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onChange();
          }
        }}
        role="switch"
        aria-checked={checked}
        tabIndex={0}
      >
        <div className={styles.toggleThumb} />
      </div>
    </label>
  );
}

// ── Environment Selector ──

function EnvironmentSelector({
  onSwitch,
}: {
  onSwitch?: () => void;
}) {
  const [env, setEnv] = useState<Environment>(getEnvironment);
  const [overridden, setOverridden] = useState(isEnvironmentOverridden);
  const natural = detectEnvironment();

  const handleChange = useCallback(
    (value: Environment) => {
      setEnv(value);
      // If switching to the natural environment, clear the override
      if (value === natural) {
        setEnvironmentOverride(null);
        setOverridden(false);
      } else {
        setEnvironmentOverride(value);
        setOverridden(true);
      }
      onSwitch?.();
    },
    [natural, onSwitch],
  );

  const handleReset = useCallback(() => {
    setEnvironmentOverride(null);
    setEnv(natural);
    setOverridden(false);
    onSwitch?.();
  }, [natural, onSwitch]);

  return (
    <div>
      <div className={styles.engineSelector}>
        <button
          className={`${styles.engineOption} ${env === 'production' ? styles.engineActive : ''}`}
          onClick={() => handleChange('production')}
          type="button"
        >
          🟢 Production
        </button>
        <button
          className={`${styles.engineOption} ${env === 'staging' ? styles.engineActive : ''}`}
          onClick={() => handleChange('staging')}
          type="button"
        >
          🟠 Staging
        </button>
      </div>
      {overridden && (
        <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.8rem', color: '#999' }}>
            Override active (natural: {natural})
          </span>
          <button
            onClick={handleReset}
            style={{
              fontSize: '0.75rem',
              padding: '0.2rem 0.5rem',
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '6px',
              color: '#ccc',
              cursor: 'pointer',
            }}
            type="button"
          >
            Reset
          </button>
        </div>
      )}
    </div>
  );
}

type PreviewSwitchState =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'ready'; message: string }
  | { status: 'error'; message: string };

function PreviewBuildSelector() {
  const activePreviewPr = getCurrentPreviewPrNumber();
  const [prInput, setPrInput] = useState(() => getRequestedPreviewPr());
  const [state, setState] = useState<PreviewSwitchState>(() => {
    const failure = getPreviewActivationFailure();
    return failure?.prNumber
      ? {
          status: 'error',
          message: `PR #${failure.prNumber} failed to boot, so Talkyn switched back to stable.`,
        }
      : { status: 'idle' };
  });

  useEffect(() => {
    if (!activePreviewPr) return;
    setPrInput(String(activePreviewPr));
  }, [activePreviewPr]);

  const normalizedInput = prInput.trim();
  const inputPrNumber = /^\d+$/u.test(normalizedInput)
    ? Number.parseInt(normalizedInput, 10)
    : null;
  const previewPath = inputPrNumber ? buildPreviewBasePath(inputPrNumber) : '/app/pr-123/';

  const handleInputChange = useCallback((value: string) => {
    const digitsOnly = value.replace(/\D+/gu, '');
    setPrInput(digitsOnly);
    setRequestedPreviewPr(digitsOnly);
    if (state.status === 'error') {
      setState({ status: 'idle' });
    }
  }, [state.status]);

  const handleOpenPreview = useCallback(async () => {
    if (!inputPrNumber) {
      setState({ status: 'error', message: 'Enter a PR number first.' });
      return;
    }

    clearPreviewActivationFailure();
    setRequestedPreviewPr(String(inputPrNumber));
    setState({ status: 'checking' });

    const version = await fetchPreviewBuildVersion(inputPrNumber);
    if (!version) {
      setState({
        status: 'error',
        message: `No deployed frontend preview was found for PR #${inputPrNumber}.`,
      });
      return;
    }

    const targetUrl = beginPreviewActivation(inputPrNumber);
    setState({
      status: 'ready',
      message: `Opening PR #${inputPrNumber} (${version})…`,
    });
    window.location.assign(targetUrl);
  }, [inputPrNumber]);

  const handleBackToStable = useCallback(() => {
    clearPreviewActivationFailure();
    clearPendingPreviewActivation();
    clearSelectedPreviewPr();
    clearRequestedPreviewPr();
    window.location.assign(buildStableUrl());
  }, []);

  const handleClearInput = useCallback(() => {
    setPrInput('');
    clearRequestedPreviewPr();
    clearPreviewActivationFailure();
    setState({ status: 'idle' });
  }, []);

  const statusText = activePreviewPr
    ? `Running preview build for PR #${activePreviewPr}`
    : 'Running stable build';

  const openLabel = activePreviewPr && activePreviewPr === inputPrNumber
    ? 'Reload preview'
    : 'Open preview';

  const statusMessage = state.status === 'ready' || state.status === 'error'
    ? state.message
    : null;

  return (
    <div>
      <div className={styles.statusRow}>
        <span
          className={styles.statusDot}
          style={{
            background: activePreviewPr ? '#a855f7' : '#35ff6b',
            boxShadow: activePreviewPr ? '0 0 8px rgba(168, 85, 247, 0.45)' : '0 0 6px rgba(53, 255, 107, 0.4)',
          }}
        />
        <span className={styles.statusText}>{statusText}</span>
      </div>

      <div className={styles.apiKeyContainer} style={{ marginTop: '0.75rem' }}>
        <div className={styles.apiKeyInputRow}>
          <input
            type="text"
            className={styles.apiKeyInput}
            value={prInput}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder="PR number…"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            spellCheck={false}
          />
          {prInput && (
            <button
              className={styles.apiKeyClear}
              onClick={handleClearInput}
              aria-label="Clear preview PR number"
              type="button"
            >
              ✕
            </button>
          )}
        </div>
        <span className={styles.apiKeyHint}>
          Preview path: {previewPath}
        </span>
        <span className={styles.apiKeyHint}>
          Frontend-only v1: preview builds reuse the current backend/environment on this origin.
        </span>
      </div>

      <button
        className={styles.updateBtn}
        onClick={handleOpenPreview}
        type="button"
        disabled={state.status === 'checking'}
        style={{ marginTop: '0.75rem' }}
      >
        {state.status === 'checking' ? 'Checking preview…' : openLabel}
      </button>

      {activePreviewPr && (
        <button
          className={styles.updateBtn}
          onClick={handleBackToStable}
          type="button"
          style={{
            marginTop: '0.5rem',
            background: 'rgba(168, 85, 247, 0.12)',
            borderColor: 'rgba(168, 85, 247, 0.35)',
            color: '#d8b4fe',
          }}
        >
          Back to stable
        </button>
      )}

      {statusMessage && (
        <div
          style={{
            marginTop: '0.75rem',
            fontSize: '12px',
            lineHeight: 1.5,
            color: state.status === 'error' ? '#fca5a5' : '#cbd5e1',
          }}
        >
          {statusMessage}
        </div>
      )}
    </div>
  );
}

// ── Version Info Display ──

function VersionDisplay({
  gatewayVersion,
}: {
  gatewayVersion?: string | null;
}) {
  const info = useVersionInfo(gatewayVersion);

  const rows: Array<{ label: string; value: string | null }> = [
    {
      label: 'Build source',
      value: info.previewPrNumber ? `Preview PR #${info.previewPrNumber}` : 'Stable',
    },
    { label: 'Web app', value: info.frontend },
    { label: 'Backend', value: info.backend },
    { label: 'Android app', value: info.android },
    { label: 'Gateway', value: info.gateway },
  ];

  return (
    <div style={{ fontSize: '0.85rem' }}>
      {rows.map(({ label, value }) => (
        <div
          key={label}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '0.3rem 0',
            borderBottom: '1px solid rgba(255,255,255,0.05)',
          }}
        >
          <span style={{ color: '#999' }}>{label}</span>
          <span style={{ color: value ? '#e0e0e0' : '#555', fontFamily: 'monospace' }}>
            {value ?? 'n/a'}
          </span>
        </div>
      ))}
      {info.buildTime && (
        <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#555' }}>
          Built: {new Date(info.buildTime).toLocaleString()}
        </div>
      )}
    </div>
  );
}

// ── Custom Backend URL (Advanced) ──

const DEFAULT_BACKEND_URL = getApiUrl();
const BACKEND_URL_KEY = STORAGE_KEYS.CUSTOM_BACKEND_URL;

function CustomBackendUrl() {
  const [expanded, setExpanded] = useState(false);
  const [url, setUrl] = useState(() =>
    localStorage.getItem(BACKEND_URL_KEY) || DEFAULT_BACKEND_URL,
  );

  const isCustom = url !== DEFAULT_BACKEND_URL;

  const handleSave = useCallback((value: string) => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === DEFAULT_BACKEND_URL) {
      localStorage.removeItem(BACKEND_URL_KEY);
      setUrl(DEFAULT_BACKEND_URL);
    } else {
      localStorage.setItem(BACKEND_URL_KEY, trimmed);
      setUrl(trimmed);
    }
  }, []);

  const handleReset = useCallback(() => {
    localStorage.removeItem(BACKEND_URL_KEY);
    setUrl(DEFAULT_BACKEND_URL);
  }, []);

  return (
    <div className={styles.apiKeyContainer}>
      <div
        className={styles.toggle}
        onClick={() => setExpanded(!expanded)}
        style={{ cursor: 'pointer', padding: '6px 0' }}
      >
        <span className={styles.toggleLabel} style={{ fontSize: '13px', color: '#888' }}>
          Custom backend URL {isCustom && '•'}
        </span>
        <span style={{ color: '#555', fontSize: '12px' }}>{expanded ? '▾' : '▸'}</span>
      </div>
      {expanded && (
        <>
          <div className={styles.apiKeyInputRow}>
            <input
              type="text"
              className={styles.apiKeyInput}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onBlur={(e) => handleSave(e.target.value)}
              placeholder={DEFAULT_BACKEND_URL}
              autoComplete="off"
              spellCheck={false}
            />
            {isCustom && (
              <button
                className={styles.apiKeyClear}
                onClick={handleReset}
                aria-label="Reset to default"
                type="button"
              >
                ✕
              </button>
            )}
          </div>
          <span className={styles.apiKeyHint}>
            {isCustom ? `Using: ${url}` : `Default: ${DEFAULT_BACKEND_URL}`}
          </span>
        </>
      )}
    </div>
  );
}

// ── Update Check Button (Android only) ──

type UpdateCheckState =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'available'; version: string; downloadUrl: string }
  | { status: 'upToDate' }
  | { status: 'error' };

function UpdateCheckButton() {
  const [state, setState] = useState<UpdateCheckState>({ status: 'idle' });
  const [isAndroid, setIsAndroid] = useState(false);

  // Detect Capacitor Android on mount
  useEffect(() => {
    const cap = (window as unknown as Record<string, unknown>).Capacitor as
      | { getPlatform?: () => string }
      | undefined;
    setIsAndroid(cap?.getPlatform?.() === 'android');
  }, []);

  const handleCheck = useCallback(async () => {
    setState({ status: 'checking' });
    try {
      const result = await checkForAppUpdate();
      if (!result) {
        setState({ status: 'error' });
      } else if (result.updateAvailable) {
        setState({
          status: 'available',
          version: result.latestVersion,
          downloadUrl: result.downloadUrl,
        });
        return; // Don't auto-reset
      } else {
        setState({ status: 'upToDate' });
      }
    } catch {
      setState({ status: 'error' });
    }
    // Reset after 3 seconds for non-available states
    setTimeout(() => setState({ status: 'idle' }), 3000);
  }, []);

  const handleDownload = useCallback(() => {
    if (state.status === 'available') {
      window.open(state.downloadUrl, '_system');
    }
  }, [state]);

  if (!isAndroid) return null;

  let label: string;
  let className = styles.updateBtn;

  switch (state.status) {
    case 'idle':
      label = 'Check for updates';
      break;
    case 'checking':
      label = 'Checking…';
      className += ` ${styles.updateBtnChecking}`;
      break;
    case 'available':
      label = `Update available: v${state.version}`;
      className += ` ${styles.updateBtnAvailable}`;
      break;
    case 'upToDate':
      label = '✓ Up to date';
      className += ` ${styles.updateBtnUpToDate}`;
      break;
    case 'error':
      label = 'Check failed';
      className += ` ${styles.updateBtnError}`;
      break;
  }

  return (
    <button
      className={className}
      onClick={state.status === 'available' ? handleDownload : handleCheck}
      type="button"
      disabled={state.status === 'checking'}
    >
      {label}
    </button>
  );
}

// ── Settings Panel ──

export function Settings({
  isOpen,
  onClose,
  selectedModel = 'default',
  onModelChange,
  privacyMode = false,
  onPrivacyToggle,
  showTimestamps = true,
  onTimestampsToggle,
  connectionStatus = 'Disconnected',
  gatewayConnectionState = 'disconnected',
  isRequestActive = false,
  sttProvider = 'none',
  onApiKeyChange,
  gateways,
  activeGatewayId,
  connectionStates,
  onGatewaySelect,
  onGatewayRemove,
  onGatewayAdd,
  onSignIn,
  gatewayVersion,
  gatewayClient = null,
  activeGatewayName = null,
  activeSessionKey = null,
  voiceLock,
}: SettingsProps) {
  const { isAuthenticated } = useAuth();

  // TTS Debug toggle state
  const [ttsDebugEnabled, setTtsDebugEnabled] = useState(
    () => localStorage.getItem(STORAGE_KEYS.TTS_DEBUG) === 'true'
  );

  // Show Thinking Stream toggle state (default: true)
  const [showThinkingStream, setShowThinkingStream] = useState(
    () => localStorage.getItem(STORAGE_KEYS.SHOW_THINKING_STREAM) !== 'false'
  );

  // Remote debug logging toggle (default: enabled)
  const [debugLoggingEnabled, setDebugLoggingEnabled] = useState(
    () => localStorage.getItem(DEBUG_LOGGING_KEY) !== 'false'
  );

  // Sub-agent bubble mode (default: follow)
  const [bubbleMode, setBubbleMode] = useState<'split' | 'follow'>(
    () => (localStorage.getItem(STORAGE_KEYS.SUBAGENT_BUBBLE_MODE) as 'split' | 'follow') ?? 'follow',
  );

  const [quickSwitchSide, setQuickSwitchSide] = useSetting(STORAGE_KEYS.SESSION_QUICK_SWITCH_SIDE);
  const [sessionSwitchReadout, setSessionSwitchReadout] = useSetting(STORAGE_KEYS.SESSION_SWITCH_READOUT);
  const [sendWhileRunningMode, setSendWhileRunningMode] = useSetting(STORAGE_KEYS.SEND_WHILE_RUNNING_MODE);
  const resolvedQuickSwitchSide = quickSwitchSide === 'left' ? 'left' : 'right';
  const readUnheardMessagesOnSessionSwitch = sessionSwitchReadout !== 'false';
  const resolvedSendWhileRunningMode = normalizeSendWhileRunningMode(sendWhileRunningMode);

  // Sub-agent depth level: '0'=Off, '1'=Leads, '2'=Agents, '3'=All
  const [subagentDepth, setSubagentDepth] = useState<'0' | '1' | '2' | '3'>(
    () => (localStorage.getItem(STORAGE_KEYS.SUBAGENT_DEPTH) ?? '0') as '0' | '1' | '2' | '3',
  );

  // Counter to trigger STT selector re-evaluation when API key changes
  const [sttRefresh, setSttRefresh] = useState(0);

  const handleKeyChange = useCallback(() => {
    setSttRefresh((c) => c + 1);
    onApiKeyChange?.();
  }, [onApiKeyChange]);

  // Handle backdrop click and escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const hasMultiGatewayProps =
    gateways && connectionStates && onGatewaySelect && onGatewayRemove && onGatewayAdd;
  const handleSignIn = () => {
    onClose();
    onSignIn?.();
  };

  return (
    <>
      {/* Backdrop */}
      <div className={styles.backdrop} onClick={onClose} />

      {/* Panel */}
      <div className={styles.panel}>
        <div className={styles.handle} />

        <div className={styles.content}>
          {/* Account / Cloud */}
          <div className={styles.section}>
            <label className={styles.label}>Account</label>
            <CloudSettings onSignIn={handleSignIn} />
          </div>

          {/* Gateway Management */}
          {hasMultiGatewayProps ? (
            <div className={styles.section}>
              <label className={styles.label}>Gateways</label>
              <GatewayList
                gateways={gateways}
                activeGatewayId={activeGatewayId ?? null}
                connectionStates={connectionStates}
                onSelect={onGatewaySelect}
                onRemove={onGatewayRemove}
                onAdd={onGatewayAdd}
              />
            </div>
          ) : (
            /* Fallback: simple connection status (backward compat) */
            <div className={styles.section}>
              <label className={styles.label}>Gateway</label>
              <div className={styles.statusRow}>
                <span
                  className={`${styles.statusDot} ${
                    connectionStatus === 'Connected'
                      ? styles.statusConnected
                      : styles.statusDisconnected
                  }`}
                />
                <span className={styles.statusText}>{connectionStatus}</span>
              </div>
            </div>
          )}

          <div className={styles.section}>
            <label className={styles.label}>Connect Pages to your AI</label>
            <PagesProvisioningSettings
              isOpen={isOpen}
              onSignIn={handleSignIn}
              gatewayClient={gatewayClient}
              connectionState={connectionStates?.get(activeGatewayId ?? '') ?? gatewayConnectionState}
              activeGatewayName={activeGatewayName ?? gateways?.find((gateway) => gateway.id === activeGatewayId)?.name ?? null}
              activeSessionKey={activeSessionKey}
            />
          </div>

          {/* GitHub Integration */}
          <div className={styles.section}>
            <GitHubSettingsWithBoundary />
          </div>

          {/* TTS Provider */}
          <div className={styles.section}>
            <label className={styles.label}>Text-to-Speech</label>
            <TtsProviderSelector onProviderChange={onApiKeyChange} />
            {!isAuthenticated && <EdgeTtsConfigInput onConfigChange={onApiKeyChange} />}
          </div>

          {/* Language & Voice */}
          <div className={styles.section}>
            <label className={styles.label}>Language & Voice</label>
            <LanguageVoiceSettings />
          </div>

          {/* STT Engine + OpenAI API Key */}
          <div className={styles.section}>
            <label className={styles.label}>
              Speech-to-Text
              <SttProviderBadge provider={sttProvider} />
            </label>
            <SttEngineSelector onEngineChange={onApiKeyChange} refreshTrigger={sttRefresh} />
            <OpenAIKeyInput onKeyChange={handleKeyChange} />
          </div>

          {/* Model Selector */}
          <div className={styles.section}>
            <label className={styles.label}>Model</label>
            <ModelSelector
              selectedModel={selectedModel}
              onModelChange={onModelChange ?? (() => {})}
              disabled={isRequestActive}
            />
          </div>

          {/* Playback Speed */}
          <div className={styles.section}>
            <label className={styles.label}>Playback Speed</label>
            <SpeedControl />
          </div>

          {/* VAD Sensitivity */}
          <div className={styles.section}>
            <label className={styles.label}>VAD Sensitivity</label>
            <VadSensitivity />
          </div>

          {/* Voice Lock */}
          {voiceLock && <VoiceLockSettings voiceLock={voiceLock} />}

          {/* Agent Messages — Show in Main Chat */}
          <div className={styles.section}>
            <label className={styles.label}>Show in Main Chat</label>
            <div className={styles.engineSelector}>
              {([['0', 'Off'], ['1', 'Leads'], ['2', 'Agents'], ['3', 'All']] as const).map(([value, label]) => (
                <button
                  key={value}
                  className={`${styles.engineOption} ${subagentDepth === value ? styles.engineActive : ''}`}
                  onClick={() => {
                    setSubagentDepth(value);
                    localStorage.setItem(STORAGE_KEYS.SUBAGENT_DEPTH, value);
                    window.dispatchEvent(new CustomEvent('subagent-depth-change', { detail: value }));
                  }}
                  type="button"
                >
                  {label}
                </button>
              ))}
            </div>
            <span className={styles.apiKeyHint}>
              {subagentDepth === '0' && 'Sub-agent messages hidden'}
              {subagentDepth === '1' && 'Show lead agents in main chat'}
              {subagentDepth === '2' && 'Show leads + coding agents in main chat'}
              {subagentDepth === '3' && 'Show all sub-agent levels in main chat'}
            </span>
            {subagentDepth !== '0' && (
              <>
                <div className={styles.engineSelector}>
                  <button
                    className={`${styles.engineOption} ${bubbleMode === 'follow' ? styles.engineActive : ''}`}
                    onClick={() => {
                      setBubbleMode('follow');
                      localStorage.setItem(STORAGE_KEYS.SUBAGENT_BUBBLE_MODE, 'follow');
                    }}
                    type="button"
                  >
                    📌 Follow
                  </button>
                  <button
                    className={`${styles.engineOption} ${bubbleMode === 'split' ? styles.engineActive : ''}`}
                    onClick={() => {
                      setBubbleMode('split');
                      localStorage.setItem(STORAGE_KEYS.SUBAGENT_BUBBLE_MODE, 'split');
                    }}
                    type="button"
                  >
                    ✂️ Split
                  </button>
                </div>
                <span className={styles.apiKeyHint}>
                  {bubbleMode === 'follow'
                    ? 'Follow: bubble moves to bottom on each update'
                    : 'Split: new bubble after 2s pause'}
                </span>
              </>
            )}
          </div>

          {/* Quick-switch tabs */}
          <div className={styles.section}>
            <label className={styles.label}>Quick-switch Tabs</label>
            <div className={styles.engineSelector}>
              <button
                className={`${styles.engineOption} ${resolvedQuickSwitchSide === 'left' ? styles.engineActive : ''}`}
                onClick={() => setQuickSwitchSide('left')}
                type="button"
              >
                ⬅️ Left
              </button>
              <button
                className={`${styles.engineOption} ${resolvedQuickSwitchSide === 'right' ? styles.engineActive : ''}`}
                onClick={() => setQuickSwitchSide('right')}
                type="button"
              >
                Right ➡️
              </button>
            </div>
            <span className={styles.apiKeyHint}>
              Dock the compact session tabs to the left or right edge of the chat view.
            </span>
          </div>

          <div className={styles.section}>
            <ToggleSwitch
              checked={readUnheardMessagesOnSessionSwitch}
              onChange={() => {
                setSessionSwitchReadout(readUnheardMessagesOnSessionSwitch ? 'false' : 'true');
                window.dispatchEvent(new Event('settings-changed'));
              }}
              label="Read unheard messages on session switch"
            />
          </div>

          <div className={styles.section}>
            <label className={styles.label}>Send While Reply Is Running</label>
            <div className={styles.engineSelector}>
              <button
                className={`${styles.engineOption} ${resolvedSendWhileRunningMode === 'queue' ? styles.engineActive : ''}`}
                onClick={() => setSendWhileRunningMode('queue')}
                type="button"
              >
                Queue normally
              </button>
              <button
                className={`${styles.engineOption} ${resolvedSendWhileRunningMode === 'steer' ? styles.engineActive : ''}`}
                onClick={() => setSendWhileRunningMode('steer')}
                type="button"
              >
                Steer current reply
              </button>
              <button
                className={`${styles.engineOption} ${resolvedSendWhileRunningMode === 'interrupt' ? styles.engineActive : ''}`}
                onClick={() => setSendWhileRunningMode('interrupt')}
                type="button"
              >
                Interrupt + replace
              </button>
            </div>
            <span className={styles.apiKeyHint}>
              {resolvedSendWhileRunningMode === 'queue' && 'Current behavior: send a normal follow-up after the active reply finishes.'}
              {resolvedSendWhileRunningMode === 'steer' && 'While the assistant is already streaming, new plain-text follow-ups steer the current reply at the next safe boundary.'}
              {resolvedSendWhileRunningMode === 'interrupt' && 'Stop the active reply and start a fresh one from the new message.'}
            </span>
          </div>

          {/* Toggles */}
          <div className={styles.section}>
            {onPrivacyToggle && (
              <ToggleSwitch
                checked={privacyMode}
                onChange={onPrivacyToggle}
                label="Privacy Mode"
              />
            )}
            {onTimestampsToggle && (
              <ToggleSwitch
                checked={showTimestamps}
                onChange={onTimestampsToggle}
                label="Show Timestamps"
              />
            )}
            <ToggleSwitch
              checked={showThinkingStream}
              onChange={() => {
                const newVal = !showThinkingStream;
                setShowThinkingStream(newVal);
                if (newVal) {
                  localStorage.removeItem(STORAGE_KEYS.SHOW_THINKING_STREAM);
                } else {
                  localStorage.setItem(STORAGE_KEYS.SHOW_THINKING_STREAM, 'false');
                }
                window.dispatchEvent(new Event('storage'));
              }}
              label="Show Thinking Stream"
            />
            <ToggleSwitch
              checked={ttsDebugEnabled}
              onChange={() => {
                const newVal = !ttsDebugEnabled;
                setTtsDebugEnabled(newVal);
                if (newVal) {
                  localStorage.setItem(STORAGE_KEYS.TTS_DEBUG, 'true');
                } else {
                  localStorage.removeItem(STORAGE_KEYS.TTS_DEBUG);
                }
                window.dispatchEvent(new Event('storage'));
              }}
              label="TTS Debug"
            />
            <ToggleSwitch
              checked={debugLoggingEnabled}
              onChange={() => {
                const newVal = !debugLoggingEnabled;
                setDebugLoggingEnabled(newVal);
                setRemoteLoggingEnabled(newVal);
              }}
              label="Remote Debug Logging"
            />
          </div>

          {/* Environment */}
          {!isAuthenticated && (
            <div className={styles.section}>
              <label className={styles.label}>Environment</label>
              <EnvironmentSelector onSwitch={() => {
                // Reload to apply new API endpoints
                reloadApp();
              }} />
            </div>
          )}

          {/* Frontend preview switching */}
          <div className={styles.section}>
            <label className={styles.label}>Preview Builds</label>
            <PreviewBuildSelector />
          </div>

          {/* Version Info */}
          <div className={styles.section}>
            <label className={styles.label}>About</label>
            <VersionDisplay gatewayVersion={gatewayVersion} />
            <UpdateCheckButton />
          </div>

          {/* Advanced — Custom Backend URL */}
          {!isAuthenticated && (
            <div className={styles.section}>
              <label className={styles.label}>Advanced</label>
              <CustomBackendUrl />
            </div>
          )}

          {/* Close Button */}
          <button className={styles.closeBtn} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );
}
